import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatDateOnly } from '../../utils/dateUtils';

// Sorting helpers
const handleSort = (field, currentField, currentDirection, setField, setDirection) => {
  if (currentField === field) {
    setDirection(currentDirection === 'asc' ? 'desc' : 'asc');
  } else {
    setField(field);
    setDirection('asc');
  }
};

const getSortIcon = (field, currentField, currentDirection) => {
  if (currentField !== field) return null;
  return currentDirection === 'asc' ? ' ▲' : ' ▼';
};

const sortData = (data, field, direction) => {
  if (!field) return data;

  return [...data].sort((a, b) => {
    let aVal, bVal;

    if (field === 'category') {
      const categoryCompare = (a.category_name || '').localeCompare(b.category_name || '');
      if (categoryCompare !== 0) {
        return direction === 'asc' ? categoryCompare : -categoryCompare;
      }
      return (a.display_order || 0) - (b.display_order || 0);
    }

    if (field === 'created_at') {
      aVal = new Date(a[field]);
      bVal = new Date(b[field]);
    } else if (field === 'is_active') {
      aVal = a[field] ? 1 : 0;
      bVal = b[field] ? 1 : 0;
    } else if (field === 'response_count') {
      aVal = parseInt(a[field]) || 0;
      bVal = parseInt(b[field]) || 0;
    } else {
      aVal = a[field];
      bVal = b[field];
    }

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    if (aVal instanceof Date && bVal instanceof Date) {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    const stringA = String(aVal).toLowerCase();
    const stringB = String(bVal).toLowerCase();
    const comparison = stringA.localeCompare(stringB);
    return direction === 'asc' ? comparison : -comparison;
  });
};

function AdminPrompts({ timezone }) {
  const [prompts, setPrompts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [isAddingPrompt, setIsAddingPrompt] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [promptFormData, setPromptFormData] = useState({
    promptText: '',
    categoryId: '',
    displayOrder: 0,
    isActive: true,
    exportName: ''
  });
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);

  useEffect(() => {
    fetchPrompts();
    fetchCategories();
  }, [selectedCategoryFilter]);

  const fetchPrompts = async () => {
    try {
      const config = selectedCategoryFilter !== 'all' ? { params: { categoryId: selectedCategoryFilter } } : {};
      const response = await api.get('/admin/prompts', config);
      setPrompts(response.data);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`Failed to fetch prompts: ${errorMsg}`);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const openAddPromptModal = () => {
    setPromptFormData({
      promptText: '',
      categoryId: selectedCategoryFilter !== 'all' ? selectedCategoryFilter : '',
      displayOrder: prompts.length,
      isActive: true,
      exportName: ''
    });
    setIsAddingPrompt(true);
  };

  const openEditPromptModal = (prompt) => {
    setEditingPrompt(prompt);
    setPromptFormData({
      promptText: prompt.prompt_text,
      categoryId: prompt.category_id,
      displayOrder: prompt.display_order,
      isActive: prompt.is_active,
      exportName: prompt.export_name || ''
    });
  };

  const closePromptModal = () => {
    setEditingPrompt(null);
    setIsAddingPrompt(false);
    setPromptFormData({
      promptText: '',
      categoryId: '',
      displayOrder: 0,
      isActive: true,
      exportName: ''
    });
  };

  const handleSavePrompt = async (e) => {
    e.preventDefault();

    try {
      if (isAddingPrompt) {
        await api.post('/admin/prompts', promptFormData);
        alert('Prompt created successfully');
      } else {
        await api.put(`/admin/prompts/${editingPrompt.id}`, promptFormData);
        alert('Prompt updated successfully');
      }

      fetchPrompts();
      closePromptModal();
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert('Failed to save prompt: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteOrRetirePrompt = async (promptId, promptText, responseCount) => {
    const action = responseCount > 0 ? 'retire' : 'delete';
    const message = responseCount > 0
      ? `This prompt has ${responseCount} response(s). It will be retired (marked inactive) instead of deleted. Continue?`
      : `Are you sure you want to delete prompt "${promptText}"? This action cannot be undone.`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      const response = await api.delete(`/admin/prompts/${promptId}`);
      fetchPrompts();
      alert(response.data.message);
    } catch (error) {
      console.error('Error deleting/retiring prompt:', error);
      alert('Failed to ' + action + ' prompt: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUnretirePrompt = async (promptId, promptText) => {
    if (!window.confirm(`Are you sure you want to reactivate prompt "${promptText}"?`)) {
      return;
    }

    try {
      await api.put(`/admin/prompts/${promptId}`, { isActive: true });
      fetchPrompts();
      alert('Prompt reactivated successfully');
    } catch (error) {
      console.error('Error unretiring prompt:', error);
      alert('Failed to reactivate prompt: ' + (error.response?.data?.error || error.message));
    }
  };

  // CSV Import/Export
  const handleExportCsv = async () => {
    try {
      const response = await api.get('/admin/prompts/export', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'prompts_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting prompts:', error);
      alert('Failed to export prompts: ' + (error.response?.data?.error || error.message));
    }
  };

  const openImportDialog = () => {
    setShowImportDialog(true);
    setImportFile(null);
    setImportResults(null);
    setUpdateExisting(false);
  };

  const closeImportDialog = () => {
    setShowImportDialog(false);
    setImportFile(null);
    setImportResults(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      setImportFile(file);
      setImportResults(null);
    }
  };

  const handleImportCsv = async () => {
    if (!importFile) {
      alert('Please select a CSV file to import');
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await api.post(
        `/admin/prompts/import?upsert=${updateExisting}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResults(response.data.results);
      fetchPrompts();

      if (response.data.results.skipped === 0) {
        const parts = [];
        if (response.data.results.imported > 0) parts.push(`${response.data.results.imported} imported`);
        if (response.data.results.updated > 0) parts.push(`${response.data.results.updated} updated`);
        alert(`Success! ${parts.join(', ')}.`);
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('Failed to import CSV: ' + (error.response?.data?.error || error.message));
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <div className="admin-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Prompt Management</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <select
              value={selectedCategoryFilter}
              onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.icon ? `${category.icon} ` : ''}{category.name}
                </option>
              ))}
            </select>
            <button
              className="btn-edit"
              onClick={handleExportCsv}
              style={{ marginBottom: 0, backgroundColor: '#0284c7' }}
            >
              📤 Export CSV
            </button>
            <button
              className="btn-edit"
              onClick={openImportDialog}
              style={{ marginBottom: 0, backgroundColor: '#059669' }}
            >
              📥 Import CSV
            </button>
            <button
              className="btn-edit"
              onClick={openAddPromptModal}
              style={{ marginBottom: 0 }}
            >
              + Add Prompt
            </button>
          </div>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort('prompt_text', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Prompt Text{getSortIcon('prompt_text', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('category', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Category{getSortIcon('category', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('display_order', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Display Order{getSortIcon('display_order', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('is_active', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Status{getSortIcon('is_active', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('response_count', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Responses{getSortIcon('response_count', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('created_by_username', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Created By{getSortIcon('created_by_username', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('created_at', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Created Date{getSortIcon('created_at', sortField, sortDirection)}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortData(prompts, sortField, sortDirection).map(prompt => (
              <tr key={prompt.id}>
                <td>{prompt.prompt_text}</td>
                <td>
                  {prompt.category_icon ? `${prompt.category_icon} ` : ''}
                  {prompt.category_name}
                </td>
                <td>{prompt.display_order}</td>
                <td>
                  {prompt.is_active ? (
                    <span style={{ color: '#10b981', fontWeight: 500 }}>Active</span>
                  ) : (
                    <span style={{ color: '#6b7280', fontWeight: 500 }}>Retired</span>
                  )}
                </td>
                <td>{prompt.response_count}</td>
                <td>{prompt.created_by_username || 'Unknown'}</td>
                <td>{prompt.created_at ? formatDateOnly(prompt.created_at, timezone) : 'N/A'}</td>
                <td>
                  <button
                    className="btn-edit"
                    onClick={() => openEditPromptModal(prompt)}
                  >
                    Edit
                  </button>
                  {prompt.is_active ? (
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteOrRetirePrompt(prompt.id, prompt.prompt_text, prompt.response_count)}
                    >
                      {parseInt(prompt.response_count) > 0 ? 'Retire' : 'Delete'}
                    </button>
                  ) : (
                    <button
                      className="btn-edit"
                      onClick={() => handleUnretirePrompt(prompt.id, prompt.prompt_text)}
                      style={{ backgroundColor: '#10b981' }}
                    >
                      Unretire
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(editingPrompt || isAddingPrompt) && (
        <div className="modal-overlay" onClick={closePromptModal}>
          <div className="modal-content admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isAddingPrompt ? 'Add New Prompt' : 'Edit Prompt'}</h3>
              <button onClick={closePromptModal}>✕</button>
            </div>

            <form onSubmit={handleSavePrompt} className="admin-form">
              <div className="form-group">
                <label>Prompt Text *</label>
                <textarea
                  value={promptFormData.promptText}
                  onChange={(e) => setPromptFormData({ ...promptFormData, promptText: e.target.value })}
                  required
                  placeholder="Enter the prompt text..."
                  rows="4"
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={promptFormData.categoryId}
                  onChange={(e) => setPromptFormData({ ...promptFormData, categoryId: e.target.value })}
                  required
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    width: '100%'
                  }}
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.icon ? `${category.icon} ` : ''}{category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={promptFormData.displayOrder}
                  onChange={(e) => setPromptFormData({ ...promptFormData, displayOrder: parseInt(e.target.value) || 0 })}
                  min="0"
                  placeholder="0"
                />
                <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Lower numbers appear first in the list
                </small>
              </div>

              <div className="form-group">
                <label className="checkbox-label" style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={promptFormData.isActive}
                    onChange={(e) => setPromptFormData({ ...promptFormData, isActive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
                <small style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                  Only active prompts are shown to users
                </small>
              </div>

              <div className="form-group">
                <label>Export Name</label>
                <input
                  type="text"
                  value={promptFormData.exportName}
                  onChange={(e) => setPromptFormData({ ...promptFormData, exportName: e.target.value })}
                  placeholder="e.g., favorite-vacation"
                  maxLength="100"
                />
                <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Used in exported media file naming. Falls back to prompt-{'{id}'} if blank.
                </small>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-save">
                  {isAddingPrompt ? 'Create Prompt' : 'Save Changes'}
                </button>
                <button type="button" onClick={closePromptModal} className="btn-cancel">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImportDialog && (
        <div className="modal-overlay" onClick={closeImportDialog}>
          <div className="modal-content admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Import Prompts from CSV</h3>
              <button onClick={closeImportDialog}>✕</button>
            </div>

            <div className="admin-form" style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ marginBottom: '0.75rem' }}>CSV Format Requirements</h4>
                <ul style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginLeft: '1.25rem', lineHeight: '1.6' }}>
                  <li><strong>Required column:</strong> prompt_text</li>
                  <li><strong>Category:</strong> Use either category_id or category (name/slug)</li>
                  <li><strong>Optional columns:</strong> id, display_order, is_active, theme, emotional_weight</li>
                  <li><strong>ID:</strong> Leave blank to auto-generate unique IDs</li>
                  <li><strong>Emotional weight:</strong> Integer from 1 (light) to 10 (heavy)</li>
                  <li><strong>Ignored:</strong> created_at, created_by (uses current values)</li>
                </ul>
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  disabled={importing}
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    width: '100%',
                    fontSize: '0.875rem'
                  }}
                />
                {importFile && (
                  <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--color-primary)' }}>
                    Selected: {importFile.name} ({(importFile.size / 1024).toFixed(2)} KB)
                  </small>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="checkbox-label" style={{ flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    disabled={importing}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span>Update existing prompts (match by ID)</span>
                </label>
                <small style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                  When enabled, prompts with existing IDs will be updated instead of skipped.
                  Leave unchecked to only import new prompts.
                </small>
              </div>

              {importResults && (
                <div style={{
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  borderRadius: '0.375rem',
                  backgroundColor: importResults.skipped > 0 ? '#fef3c7' : '#d1fae5',
                  border: `1px solid ${importResults.skipped > 0 ? '#fbbf24' : '#10b981'}`
                }}>
                  <h4 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>Import Results</h4>
                  <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <strong>Total rows:</strong> {importResults.total}<br />
                    {importResults.imported > 0 && <><strong>Imported:</strong> {importResults.imported}<br /></>}
                    {importResults.updated > 0 && <><strong>Updated:</strong> {importResults.updated}<br /></>}
                    {importResults.skipped > 0 && <><strong>Skipped:</strong> {importResults.skipped}<br /></>}
                  </p>
                  {importResults.errors && importResults.errors.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <strong style={{ fontSize: '0.875rem' }}>Errors:</strong>
                      <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '0.5rem',
                        fontSize: '0.75rem',
                        backgroundColor: 'white',
                        padding: '0.5rem',
                        borderRadius: '0.25rem'
                      }}>
                        {importResults.errors.map((err, idx) => (
                          <div key={idx} style={{ marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                            <strong>Line {err.line}:</strong> {err.error}
                            <br />
                            <span style={{ color: '#6b7280' }}>
                              {err.data.prompt_text ? `"${err.data.prompt_text.substring(0, 50)}..."` : 'No prompt text'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  onClick={handleImportCsv}
                  className="btn-save"
                  disabled={!importFile || importing}
                >
                  {importing ? 'Importing...' : 'Import Prompts'}
                </button>
                <button
                  type="button"
                  onClick={closeImportDialog}
                  className="btn-cancel"
                  disabled={importing}
                >
                  {importResults ? 'Close' : 'Cancel'}
                </button>
              </div>

              <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                <small style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                  <strong>Note:</strong> See backend/sample_prompts_import.csv for an example CSV format.
                  All imports run in a transaction - if there are critical errors, no changes will be made.
                </small>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminPrompts;
